import React from 'react';
import { GraduationCap, Rocket, Users2, HeartPulse, Laptop, Globe2, TrendingUp, Gift, Star, Zap, FileCheck2, PhoneCall, Award } from 'lucide-react';

const PERKS = [
  { Icon: TrendingUp, t: 'Fast-track growth', d: 'Promotions on merit, not tenure — engineers lead projects within 18 months.' },
  { Icon: GraduationCap, t: 'Learning budget', d: 'Paid certifications (AWS, Google, Meta) and a personal upskilling allowance.' },
  { Icon: Globe2, t: 'Global projects', d: 'Ship products for clients in the USA, UK, UAE, Netherlands, Malaysia and Hong Kong from day one.' },
  { Icon: Users2, t: 'Mentor buddy', d: 'Every new joiner gets a senior mentor — no one figures it out alone.' },
  { Icon: Laptop, t: 'Hybrid & flexible', d: 'Modern offices in 5 cities plus flexible work options that respect your life.' },
  { Icon: HeartPulse, t: 'Health first', d: 'Medical insurance for you and your family, plus wellness days.' },
  { Icon: Gift, t: 'Referral rewards', d: 'Earn bonuses for bringing great people — 30% of roles are filled by referrals.' },
  { Icon: Award, t: 'Recognition culture', d: 'Quarterly awards, spot bonuses and a stage for your wins.' },
];

const PATH = [
  { step: '01', t: 'Join as a Fresher', d: 'Campus or open application — no experience needed, just curiosity.' },
  { step: '02', t: '8-Week Paid Bootcamp', d: 'Hands-on training in modern stacks with real code reviews from seniors.' },
  { step: '03', t: 'Live Project + Mentor', d: 'Contribute to real client products with a dedicated mentor beside you.' },
  { step: '04', t: 'Full Engineer in 6 Months', d: 'Own features end-to-end — and start climbing the Hipro growth ladder.' },
];

const PROCESS = [
  { Icon: FileCheck2, t: 'Apply in 2 minutes', d: 'One-click apply with your profile — no long forms.' },
  { Icon: Zap, t: 'AI screening in 48 hours', d: 'Every application reviewed fast — no black holes.' },
  { Icon: PhoneCall, t: 'Two friendly interviews', d: 'Skills chat + culture chat, scheduled around you.' },
  { Icon: Rocket, t: 'Offer within a week', d: 'Clear compensation, joining kit and your buddy assigned.' },
];

const STATS = [
  { v: '1800+', l: 'Professionals worldwide' },
  { v: '4.6★', l: 'Employee happiness score' },
  { v: '92%', l: 'Freshers confirmed after training' },
  { v: '9', l: 'Offices to grow from' },
];

const LifeAtHipro = () => (
  <>
    <section className="py-16 bg-slate-950 text-white" data-testid="life-at-hipro">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-orange-400">Life at Hipro</span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold">Do the best work of your career — with people who've got your back</h2>
          <p className="mt-4 text-slate-300">We hire for hunger, not just resumes. Freshers here don't fetch coffee — they ship products used by clients across three continents.</p>
        </div>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {STATS.map((s) => (
            <div key={s.l} className="rounded-2xl bg-white/5 border border-white/10 p-5 text-center">
              <div className="text-3xl font-extrabold text-orange-400">{s.v}</div>
              <div className="mt-1 text-xs text-slate-400">{s.l}</div>
            </div>
          ))}
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="perks-grid">
          {PERKS.map(({ Icon, t, d }) => (
            <div key={t} className="rounded-2xl bg-white/5 border border-white/10 p-5 hover:border-orange-400/50 transition-colors">
              <Icon className="h-5 w-5 text-orange-400" />
              <div className="mt-2.5 font-bold text-sm">{t}</div>
              <p className="mt-1 text-xs text-slate-400 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 bg-white" data-testid="fresher-path">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-orange-600">Fresher? Start here</span>
          <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-slate-900">From campus to shipping code in 6 months</h2>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PATH.map((p, i) => (
            <div key={p.step} className="relative rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <div className="text-4xl font-extrabold text-orange-100">{p.step}</div>
              <div className="mt-2 font-bold text-slate-900">{p.t}</div>
              <p className="mt-1.5 text-sm text-slate-600">{p.d}</p>
              {i < 3 && <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-orange-300" />}
            </div>
          ))}
        </div>
        <div className="mt-14 rounded-2xl bg-slate-50 border p-8">
          <div className="text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-orange-600">Hiring process</span>
            <h3 className="mt-2 text-xl font-extrabold text-slate-900">Respectfully fast — because your time matters</h3>
          </div>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map(({ Icon, t, d }) => (
              <div key={t} className="flex gap-3">
                <div className="h-10 w-10 shrink-0 rounded-full bg-orange-500 text-white flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                <div>
                  <div className="font-bold text-sm text-slate-900">{t}</div>
                  <p className="mt-0.5 text-xs text-slate-600">{d}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-center gap-1 text-xs text-slate-500">
            <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" /> Every applicant gets a response — guaranteed.
          </div>
        </div>
      </div>
    </section>
  </>
);

export default LifeAtHipro;
